import * as React from "react";
import { clearTab, readTab, writeTab } from "@/softphone/tab-storage";

/**
 * A callback time the rep picked during the call (ACL-559), kept until the call's outcome window
 * uses it. Picking it records nothing: the time still reaches the dialer only as the callback
 * outcome's `callback_at` (ADR-080 in `platform`), so the window opens on it when the rep chooses a
 * callback, and an outcome that is not one makes no callback.
 *
 * Module state kept for the tab, as the voicemail suggestion's planned outcome is
 * (`planned-outcome.ts`): the card it is picked on and the window it is used in are drawn apart, and
 * a reload between the hangup and the window still has it. It names the call, so a time nothing used
 * cannot be read as the next call's.
 */

export interface PickedCallback {
  /** The caller's channel: what the outcome window names the call by (`session.channel_uuid`). */
  callId: string;
  /** The instant picked, in milliseconds. */
  at: number;
  /** What the call is for, as typed (`useCallbackTime`'s purpose). */
  purpose: string;
  /** The backup times picked with it, in order, in milliseconds (ACL-559, ADR-183 and ADR-210 in
   *  `platform`); empty for none. They go with the callback outcome as its backup times, as the time
   *  goes. */
  backupTimes: number[];
}

const KEY = "acl.picked-callback";

function stored(): PickedCallback | null {
  try {
    const value: unknown = JSON.parse(readTab(KEY) ?? "null");
    if (typeof value !== "object" || value === null) return null;
    const { callId, at, purpose, backupTimes, backupAt } = value as Record<string, unknown>;
    return typeof callId === "string" && typeof at === "number" && Number.isFinite(at)
      ? {
          callId,
          at,
          purpose: typeof purpose === "string" ? purpose : "",
          // A tab kept from before there were several still has its one `backupAt`.
          backupTimes: (Array.isArray(backupTimes) ? backupTimes : [backupAt]).filter(
            (time): time is number => typeof time === "number" && Number.isFinite(time),
          ),
        }
      : null;
  } catch {
    return null;
  }
}

let picked: PickedCallback | null = stored();
const watchers = new Set<() => void>();

function changed(): void {
  for (const notify of watchers) notify();
}

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

/** The time to open this call's callback on, in place of the outcome's own wait. Without backup
 *  times said, it has none. */
export function pickCallback(next: Omit<PickedCallback, "backupTimes"> & { backupTimes?: readonly number[] }): void {
  picked = { ...next, backupTimes: [...(next.backupTimes ?? [])] };
  writeTab(KEY, JSON.stringify(picked));
  changed();
}

/** Drop the time: the call's outcome is recorded, or the rep took the time back. */
export function forgetPickedCallback(callId: string): void {
  if (picked?.callId !== callId) return;
  picked = null;
  clearTab(KEY);
  changed();
}

/** The time picked on this call, or null. */
export function pickedCallback(callId: string | null): PickedCallback | null {
  return callId !== null && picked?.callId === callId ? picked : null;
}

export function usePickedCallback(callId: string | null): PickedCallback | null {
  return React.useSyncExternalStore(watch, () => pickedCallback(callId), () => pickedCallback(callId));
}
